import React from 'react'
import { useRouter } from 'next/navigation'

import { SiteDataContext } from '../../components'
import { getMicroCopy } from '../../utils/microcopy'
import NotificationBar from '../NotificationBar'
import SideDrawer from '../SideDrawer'
import { useNavigationData } from '@/src/context/NavigationContext'
import { getUrl } from '@/src/utils/urls'
import { getNotifications } from '@/src/utils/contentful'
import { useMicroCopyData } from '@/src/context/MicroCopyContext'
import { useGlobalData } from '@/pages/_app'
import Image from 'next/image'
import Icon from '../IconNew/Icon'
import cn from 'classnames'

const isEnabled = (notification, currentISOCode) => {
  return (
    notification?.enabledMarkets.filter((market) => market.countryIsoCode === currentISOCode)
      .length > 0 && notification.enabledLocales
  )
}

export default function TopBar({ headerAction, slugPrefix = '', storybookNotifications = [] }) {
  const { toggleSideDrawer, isSideDrawerOpen } = useGlobalData()
  const { navigationItems } = useNavigationData()
  const router = useRouter()

  const [isOpen, setIsOpen] = React.useState(false)
  const [_isLoading, setIsLoading] = React.useState(false)
  const [notifications, setNotifications] = React.useState(storybookNotifications || [])
  const microCopyData = useMicroCopyData()
  const {
    isTextRtl,
    lang: pageLang,
    market: activeMarket,
    locale: activeLocale
  } = React.useContext(SiteDataContext)
  const { isoCode, flagUrl } = activeMarket || {}

  const fetchNotifications = async (isoCode) => {
    try {
      setIsLoading(true)
      const notifictions = await getNotifications(pageLang, activeMarket.id)

      if (notifictions.errors) {
        const errorMessages = notifictions.errors.map((error) => error.message).join(', ')
        throw new Error(errorMessages)
      }

      if (!notifictions) {
        setNotifications([])
        setIsLoading(false)
      }

      const enabledNotifications = notifictions
        .filter((notification) => isEnabled(notification, isoCode))
        .map((notification) => ({
          ...notification,
          url: getUrl({
            navItem: notification,
            slugPrefix: slugPrefix,
            navigationItems: navigationItems,
            isNotification: true
          })
        }))

      setNotifications(enabledNotifications || [])
      setIsLoading(false)
    } catch (error) {
      console.error(error)
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    if (!storybookNotifications?.length) {
      fetchNotifications(isoCode)
    }
  }, [activeLocale, isoCode])

  React.useEffect(() => setIsOpen(false), [router.asPath])

  const handleSelectMarket = (language, e) => {
    e.stopPropagation()
    router.push(language.slug).then(() => setIsOpen(false))
  }

  return (
    <>
      <div className={cn('c-top-bar', { 'c-top-bar--rtl': isTextRtl })}>
        <div className="c-top-bar__container">
          <div className="c-top-bar__notification-bar-container">
            {notifications.length ? <NotificationBar notifications={notifications} /> : null}
          </div>
          <div className="c-top-bar__actions-container">
            {headerAction && headerAction.url && headerAction.label && (
              <a href={headerAction.url} className="c-top-bar__action c-top-bar__action__item">
                <Icon
                  name={'tabler-square-arrow-right'}
                  size={24}
                  classes={'c-top-bar__action__icon'}
                />
                {headerAction.label}
              </a>
            )}
            <button
              className="c-top-bar__market-selector c-top-bar__action__item c-top-bar__action__button"
              onClick={() => toggleSideDrawer(!isOpen)}
            >
              {flagUrl ? (
                <Image
                  key={`${activeMarket.title} flag`}
                  priority
                  width={20}
                  height={20}
                  alt={`${activeMarket.title} flag`}
                  src={activeMarket.flagRoundUrl || activeMarket.flagUrl}
                />
              ) : null}
              <span>{pageLang}</span>
            </button>
          </div>
        </div>
      </div>
      <SideDrawer
        isOpen={isSideDrawerOpen}
        languageLocationMenu={{
          locationLabel: getMicroCopy({
            key: 'location',
            fallback: 'Location',
            data: microCopyData[pageLang]
          }),
          languageLabel: getMicroCopy({
            key: 'language',
            fallback: 'Select language',
            data: microCopyData[pageLang]
          }),
          title: getMicroCopy({
            key: 'chooseYourLocation',
            fallback: 'Select your location and language',
            data: microCopyData[pageLang]
          }),
          handleSelectMarket: handleSelectMarket
        }}
        handleIsOpen={(val) => toggleSideDrawer(val)}
      />
    </>
  )
}
